import React, { useEffect, useState } from 'react';
import { Item, AccountDetail } from '../types';
import {
  getItems, getAccountDetails, syncItemsFromAccount,
  updateItem, deleteItem, setItemMultiSpec, setItemMultiQuantityDelivery
} from '../services/api';
import { Box, RefreshCw, ShoppingBag, Edit, Trash2, Save, X, Eye, EyeOff } from 'lucide-react';
import toast from '../lib/toast';
import { formatPrice } from '../lib/format';
// PG-001：商品列表接入通用分页条 + 搜索框（前端本地过滤，默认每页 10 条）
import { PaginationBar, SearchBox, DEFAULT_PAGE_SIZE } from './Pagination';

/**
 * 商品缩略图：加载失败自动回落到占位图标。
 * 图地址走阿里云图床，万一某个 URL 失效，也不要在卡片里留一个浏览器的破图。
 */
const ItemThumb: React.FC<{ src?: string }> = ({ src }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-300">
        <Box className="w-10 h-10" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      onError={() => setFailed(true)}
    />
  );
};

const ItemList: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [editForm, setEditForm] = useState<Partial<Item>>({});
  // ITM-001：已下架商品默认隐藏。同步时会自动打「已下架」标记（只打标记、不删数据），
  // 这里给个开关，用户想看 / 想手动删除时能翻出来。
  const [showOffline, setShowOffline] = useState(false);
  // PG-001：搜索 + 分页状态（前端本地过滤，默认每页 10 条）
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const offlineCount = items.filter(i => i.is_offline).length;
  const visibleItems = showOffline ? items : items.filter(i => !i.is_offline);

  // PG-001：按标题 / 商品ID 搜索（在「是否显示已下架」的可见集合里再过滤）
  const filteredItems = visibleItems.filter(item => {
    if (!searchText.trim()) return true;
    const kw = searchText.toLowerCase().trim();
    return (
      item.item_title?.toLowerCase().includes(kw) ||
      String(item.item_id || '').toLowerCase().includes(kw)
    );
  });
  useEffect(() => { setPage(1); }, [searchText, showOffline]);
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedItems = filteredItems.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    getItems().then(setItems);
    // 只有一个账号时直接带入为默认，省得每次手工选一次
    getAccountDetails().then(list => {
      setAccounts(list);
      if (list && list.length === 1) {
        setSelectedAccount(list[0].id);
      }
    });
  }, []);

  const handleSync = async () => {
      if (!selectedAccount) return toast.warning('请先选择账号');
      setLoading(true);
      try {
        const res: any = await syncItemsFromAccount(selectedAccount);
        await getItems().then(setItems);
        // ITM-001：同步会顺带把已下架/已删除的宝贝打上标记（只标记不删数据）
        const flagged = res?.flagged_count ?? 0;
        toast.success(flagged > 0
          ? `${res?.message || '同步完成'}（${flagged} 个已标记下架，可点「显示已下架」查看）`
          : (res?.message || '同步完成'));
      } catch (e) {
        console.error('同步商品失败:', e);
        toast.error('同步失败，请重试');
      } finally {
        setLoading(false);
      }
  };

  // 统一刷新：任何写操作之后都重新拉一次，保证界面与数据库一致
  const refreshItems = async () => {
    try {
      const list = await getItems();
      setItems(list);
    } catch (error) {
      console.error('刷新商品列表失败:', error);
    }
  };

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setEditForm({ ...item });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    try {
      // 【修复】此前只改本地 state，从不调用后端 —— 点保存后界面变了，刷新就打回原形。
      // 后端当前只开放 item_detail 字段（标题/价格来自闲鱼，需改完再同步）。
      await updateItem(selectedItem.cookie_id, selectedItem.item_id, {
        item_detail: editForm.item_detail ?? selectedItem.item_detail ?? ''
      });
      toast.success('商品详情已保存');
      setShowEditModal(false);
      await refreshItems();
    } catch (error: any) {
      console.error('更新商品失败:', error);
      toast.error(`保存失败：${error?.message || '请重试'}`);
    }
  };

  const handleDelete = async (item: Item) => {
    // ITM-002：这里实际是「隐藏」而不是物理删除。原因见 db_manager.delete_item_info 注释：
    // 直接删行会让仪表盘销量排行的商品名退化成纯 item_id，还会让历史订单的自动发货
    // 因「商品不属于当前账号」被跳过。所以弹窗文案必须如实说明是隐藏、数据保留。
    if (!confirm(`确认隐藏商品"${item.item_title}"吗？\n\n数据会保留在库里（不会丢订单统计），只是从列表隐藏，之后可以重新显示。`)) return;
    try {
      await deleteItem(item.cookie_id, item.item_id);
      toast.success('商品已隐藏（数据保留，可重新显示）');
      await refreshItems();
    } catch (error: any) {
      console.error('隐藏商品失败:', error);
      toast.error(`隐藏失败：${error?.message || '请重试'}`);
      await refreshItems();
    }
  };

  // 【移除】「添加商品」功能：商品数据设计上只来自闲鱼账号同步，
  // 后端没有新建接口，此前按钮点击后弹窗 JSX 不存在导致毫无反应。需要新商品请选账号点「同步商品」。

  const toggleMultiSpec = async (item: Item) => {
    const next = !item.is_multi_spec;
    // 乐观更新 + 失败回滚
    setItems(prev => prev.map(i =>
      i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_spec: next } : i
    ));
    try {
      await setItemMultiSpec(item.cookie_id, item.item_id, next);
    } catch (error: any) {
      console.error('切换多规格状态失败:', error);
      setItems(prev => prev.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_spec: !next } : i
      ));
      toast.error(`切换失败：${error?.message || '请重试'}`);
    }
  };

  const toggleMultiQty = async (item: Item) => {
    const next = !item.is_multi_qty_ship;
    setItems(prev => prev.map(i =>
      i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_qty_ship: next } : i
    ));
    try {
      await setItemMultiQuantityDelivery(item.cookie_id, item.item_id, next);
    } catch (error: any) {
      console.error('切换多数量发货状态失败:', error);
      setItems(prev => prev.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_qty_ship: !next } : i
      ));
      toast.error(`切换失败：${error?.message || '请重试'}`);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">商品管理</h2>
          <p className="text-gray-500 mt-2 text-sm">监控并管理所有账号下的闲鱼商品。</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap justify-end">
            {/* PG-001：搜索商品（标题 / 商品ID），输入即过滤并回到第 1 页 */}
            <SearchBox value={searchText} onChange={setSearchText} placeholder="搜索商品标题/ID..." />
            {/* ITM-001：已下架商品默认隐藏；有标记时才显示这个开关 */}
            {offlineCount > 0 && (
              <button
                onClick={() => setShowOffline(v => !v)}
                className={`flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold border transition-colors ${
                  showOffline
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
                title="已下架商品只在库里打了标记，数据没删；点这里可以查看并隐藏（数据保留，不影响订单统计）"
              >
                {showOffline ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                显示已下架 ({offlineCount})
              </button>
            )}
            <select
                className="ios-input px-4 py-3 rounded-xl text-sm"
                value={selectedAccount}
                onChange={e => setSelectedAccount(e.target.value)}
            >
                <option value="">选择账号以同步</option>
                {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.nickname}</option>
                ))}
            </select>
            <button
                onClick={handleSync}
                disabled={loading || !selectedAccount}
                className="ios-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 disabled:opacity-50"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? '同步中…' : '同步商品'}
            </button>
        </div>
      </div>

      <div className="relative">
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 transition-opacity ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
          {pagedItems.map(item => (
              <div key={`${item.cookie_id}-${item.item_id}`} className={`ios-card p-4 rounded-3xl hover:shadow-lg transition-all group relative ${item.is_offline ? 'opacity-70' : ''}`}>
                  {item.is_offline && (
                    <div className="absolute top-3 left-3 z-10 bg-gray-700/85 backdrop-blur text-white text-[11px] font-bold px-2 py-1 rounded-lg">
                      已下架
                    </div>
                  )}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-[#FFE815] transition-colors"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-red-100 text-red-500 transition-colors"
                        title="隐藏（数据保留，不影响订单统计）"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                  </div>
                  <div className="aspect-square bg-gray-100 rounded-2xl mb-4 overflow-hidden relative">
                      {/* item_image 由后端返回（采集时入库，老数据从 item_raw_data 里提取）；
                          加载失败会回落到占位图标，不再出现浏览器破图 */}
                      <ItemThumb key={item.item_image || 'empty'} src={item.item_image} />
                      <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded-lg">
                          {/* item_price 里可能已自带「¥」（采集端历史数据），统一走 formatPrice 兜底，避免出现「¥¥3.88」 */}
                          {formatPrice(item.item_price)}
                      </div>
                  </div>
                  <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-2 h-10">{item.item_title}</h3>
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                      <span className="bg-gray-100 px-2 py-1 rounded-md truncate max-w-[100px]">ID: {item.item_id}</span>
                  </div>
                  <div className="flex gap-2">
                      <button
                        onClick={() => toggleMultiSpec(item)}
                        className={`flex-1 text-xs font-bold px-2 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_spec
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多规格
                      </button>
                      <button
                        onClick={() => toggleMultiQty(item)}
                        className={`flex-1 text-xs font-bold px-2 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_qty_ship
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多数量发货
                      </button>
                  </div>
              </div>
          ))}
          {filteredItems.length === 0 && (
             <div className="col-span-full py-20 text-center text-gray-400">
                 <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                 {searchText.trim() && visibleItems.length > 0
                   ? `没有匹配「${searchText}」的商品`
                   : items.length > 0 && offlineCount > 0 && !showOffline
                     ? `当前没有在售商品（${offlineCount} 个已下架，可点右上角「显示已下架」查看）`
                     : '暂无商品数据，请选择账号进行同步'}
             </div>
          )}
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/40 z-10">
          <div className="flex flex-col items-center gap-2 px-5 py-4 rounded-2xl bg-white shadow-lg border border-gray-100">
            <RefreshCw className="w-7 h-7 animate-spin text-gray-400" />
            <span className="text-sm font-medium text-gray-500">加载中…</span>
          </div>
        </div>
      )}
      </div>

      {/* PG-001：分页条（默认每页 10 条；搜索结果同样分页） */}
      {filteredItems.length > 0 && (
        <div className="ios-card rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-white">
          <div className="px-4 pt-3 text-sm text-gray-500 font-medium">
            共 <span className="text-gray-900 font-extrabold">{filteredItems.length}</span> 个商品
            {searchText.trim() && (
              <span className="ml-2 text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md">
                搜索「{searchText}」
              </span>
            )}
          </div>
          <PaginationBar
            total={filteredItems.length}
            page={safePage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        </div>
      )}

      {/* 编辑商品弹窗：此前 setShowEditModal(true) 之后根本没有任何弹窗 JSX，
          点了「编辑」按钮毫无反应。这里补上，且只开放后端真正支持的 item_detail 字段。 */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">编辑商品</h3>
              <button onClick={() => setShowEditModal(false)} className="p-1 text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-3 text-sm text-gray-600">
              <div className="truncate">商品：{selectedItem.item_title}</div>
              <div className="text-xs text-gray-400">ID: {selectedItem.item_id}</div>
            </div>

            <label className="mb-1 block text-xs font-bold text-gray-500">商品详情（用于发货规则匹配）</label>
            <textarea
              className="ios-input mb-3 h-40 w-full rounded-xl p-3 text-sm"
              value={typeof editForm.item_detail === 'string' ? editForm.item_detail : ''}
              onChange={e => setEditForm({ ...editForm, item_detail: e.target.value })}
              placeholder="填写商品详情，发货规则会按「标题 + 详情」匹配关键词"
            />
            <p className="mb-4 text-xs text-gray-400">标题与价格来自闲鱼，请在闲鱼侧修改后点「同步商品」更新。</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-bold hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> 保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemList;
